/**
 * BottomTabBar — mobile-only bottom navigation (iteration 55).
 *
 * Rendered by Layout.jsx below the `lg` breakpoint. Desktop keeps the sidebar
 * so nothing changes there. Taps route with React Router, and the "More" tab
 * re-opens the existing sidebar drawer so every feature remains reachable.
 *
 * Tabs:
 *   Home   → /
 *   Events → /events
 *   Music  → /music
 *   Chat   → /chat
 *   More   → opens sidebar drawer (admin / settings / profile / help etc.)
 */

import { Link, useLocation } from "react-router-dom";
import {
  Home,
  CalendarDays,
  Music2,
  MessageCircle,
  MoreHorizontal,
} from "lucide-react";

const TABS = [
  { key: "home", label: "Home", Icon: Home, to: "/" },
  { key: "events", label: "Events", Icon: CalendarDays, to: "/events" },
  { key: "music", label: "Music", Icon: Music2, to: "/music" },
  { key: "chat", label: "Chat", Icon: MessageCircle, to: "/chat" },
  // `more` is special — handled by onMoreClick (opens the sidebar drawer).
  { key: "more", label: "More", Icon: MoreHorizontal, to: null },
];

// Deps don't get Music / Chat tabs — they only see Dashboard and Events.
const DEP_TAB_KEYS = new Set(["home", "events", "more"]);

/** Which tab should render as active for a given pathname. */
function matchTab(pathname) {
  if (pathname === "/" || pathname === "") return "home";
  if (pathname.startsWith("/events") || pathname.startsWith("/calendar")) return "events";
  if (pathname.startsWith("/music")) return "music";
  if (pathname.startsWith("/chat")) return "chat";
  // Everything else (players, sections, admin, settings, reports, profile, ...) lives under "More"
  return "more";
}

export default function BottomTabBar({ onMoreClick, isDep = false }) {
  const location = useLocation();
  const active = matchTab(location.pathname);
  const visibleTabs = isDep ? TABS.filter((t) => DEP_TAB_KEYS.has(t.key)) : TABS;

  return (
    <nav
      className="bottom-tab-bar lg:hidden"
      role="navigation"
      aria-label="Primary"
      data-testid="bottom-tab-bar"
    >
      {visibleTabs.map(({ key, label, Icon, to }) => {
        const isActive = key === active;
        const commonClass =
          "flex-1 flex flex-col items-center justify-center gap-0.5 py-1 active:opacity-60 transition-opacity select-none";
        const color = isActive ? "text-primary" : "text-muted-foreground";
        const strokeWidth = isActive ? 2.4 : 1.9;
        const content = (
          <>
            <Icon className="w-[22px] h-[22px]" strokeWidth={strokeWidth} />
            <span className="text-[10px] font-medium leading-none">{label}</span>
          </>
        );
        if (key === "more") {
          return (
            <button
              key={key}
              type="button"
              onClick={onMoreClick}
              className={`${commonClass} ${color}`}
              data-testid={`bottom-tab-${key}`}
              aria-label={label}
            >
              {content}
            </button>
          );
        }
        return (
          <Link
            key={key}
            to={to}
            className={`${commonClass} ${color}`}
            data-testid={`bottom-tab-${key}`}
            aria-current={isActive ? "page" : undefined}
          >
            {content}
          </Link>
        );
      })}
    </nav>
  );
}
